<template>
    <brick-settings-popup
        :resources="{
            deletePromptTitle: resource('JourneyBuilder_TouchpointSettings_Delete_Title'),
            deletePromptBody: resource('JourneyBuilder_TouchpointSettings_Delete_Body'),
            brickTitle: resource('JourneyComposer_BrickSettingsTouchpoint_Title')
        }"
        :invalid="isLoading || $v.$invalid || !customConditionsValid"
        :brick="brick"
        :name.sync="newName"
        
        @save="save()"
        @delete="$emit('delete')"
        @hidden="$emit('hidden')"
        @display-errors="displayErrors()"
        
        include-actionable-report-navigation
    >
        <settings-item :label="resource('JourneyComposer_BrickSettingsTouchpoint_ParentType_DisplayName')" icon="far fa-edit">
            <template #input="{id}">
                <v-select class="form-control"
                          
                          v-model="touchpointParentTypeModel"
                          :input-id="id"
                          :options="touchPointParentTypes"
                          :disabled="!canWrite || touchPointParentTypes.length === 0"
                          :clearable="false"

                          searchable
                          filterable
                >
                    <div slot="no-options" />
                </v-select>
            </template>
            
            <template #errors>
                <required-error-message v-if="$v.settings.touchpointParentType.$dirty && $v.settings.touchpointParentType.$invalid"
                                        :field-name="resource('JourneyComposer_BrickSettingsTouchpoint_ParentType_DisplayName')"
                />
            </template>
        </settings-item>

        <settings-item :label="resource('JourneyComposer_BrickSettingsTouchpoint_Type_DisplayName')" icon="far fa-edit">
            <template #input="{id}">
                <v-select class="form-control" ref="subtype"

                          v-model="selectedTouchPointType"

                          :input-id="id"
                          label="name"
                          :options="availableTouchPointTypes"
                          :disabled="!canWrite || availableTouchPointTypes.length === 0"
                          :clearable="false"

                          searchable
                          filterable
                >
                    <div slot="no-options" />
                </v-select>
            </template>
            
            <template #errors>
                <required-error-message v-if="$v.settings.touchPointTypeId.$dirty && $v.settings.touchPointTypeId.$invalid"
                                        :field-name="resource('JourneyComposer_BrickSettingsTouchpoint_Type_DisplayName')"
                />
            </template>
        </settings-item>

        <touchpoint-campaign 
            v-if="campaignType" 
            v-model="settings.typeSettings[campaignType.propertyName]"
            
            :label="campaignType.label"
            :icon="campaignType.icon"
            :options="campaignOptions"
        />
        
        <settings-item v-if="displayGroupByTitle" :label="resource('JourneyBuilder_FilterSettings_GroupByTitle')">
            <template #input>
                <toggle
                    v-model="settings.typeSettings.groupByTitle"
                    :sync="true"
                    :labels="{checked: resource('Bool_Yes'), unchecked: resource('Bool_No')}"
                    :width="78"
                    :disabled="!canWrite"
                />
            </template>
        </settings-item>

        <settings-item v-if="displayGoalToggleForTouchpointBricks" :label="resource('JourneyBuilder_FilterSettings_Goal_DisplayName')">
            <template #input>
                <toggle
                    v-model="settings.goal"
                    :sync="true"
                    :labels="{checked: resource('Bool_Yes'), unchecked: resource('Bool_No')}"
                    :width="78"
                    :disabled="!canWrite"
                />
            </template>
        </settings-item>

        <!-- 
            Hacky workaround: wait for touchPointTypes to load
            Our CustomConditions component seemingly contains a race condition causing all
            dropdown values to be reset if it is rendered before touchPointTypes have been loaded
        -->
        <touchpoint-custom-conditions
            v-if="touchPointTypes.length"
            ref="customConditions"
            
            v-model="settings.customConditions"
            
            :parent-type="settings.touchpointParentType"
            :sub-type="selectedTouchPointType && selectedTouchPointType.name"
            
            @loading-state-changed="isLoading = $event"
            @validation-state-changed="customConditionsValid = $event"
        />

        <arrival v-model="settings.arrivalCondition" :availableConditions="availableArrivalConditions"/>
    </brick-settings-popup>
</template>

<script>
import BrickSettingsPopup from '../BrickSettingsPopup';
import Arrival from './TouchpointArrival.vue';
import InputGroup from '@core/Components/Inputs/InputGroup.vue';
import RequiredErrorMessage from "@core/Components/Errors/RequiredErrorMessage";
import TouchpointCampaign from './TouchpointCampaign';
import TouchpointCustomConditions from "./TouchpointCustomConditions";

import VSelect from 'vue-select';
import { ToggleButton } from 'vue-js-toggle-button';

import {conditionTypes} from '@modules/JourneyBuilder/ConnectionSettings/condition-types';
import brickSettingsMixin from '../Mixins/brick-settings'
import resources from '@core/Util/Mixins/resources';
import {validationMixin} from 'vuelidate';
import { required } from 'vuelidate/lib/validators'
import {mapState} from 'vuex';
import {parentTypes, subTypes} from "@modules/JourneyBuilder/Bricks/brick-types";

import api from '@modules/JourneyBuilder/api';
import { bootstrapDangerGrowl } from '@core/Util/growler';
import touchpointCampaignTypes from './touchpointCampaignTypes';
import promptYesNo from '@modules/JourneyBuilder/Util/promptYesNo';
import {FLAGS} from "@modules/JourneyBuilder/constants";

import {brickDefinitions} from "@modules/JourneyBuilder/Bricks/brick-definitions";

export default {
    name: 'brick-settings-touchpoint',
    mixins: [brickSettingsMixin, resources, validationMixin],
    components: {
        BrickSettingsPopup,
        InputGroup,
        VSelect,
        Arrival,
        'Toggle': ToggleButton,
        TouchpointCustomConditions,
        RequiredErrorMessage,
        TouchpointCampaign
    },
    async created() {
        const vm = this;

        //Assign a new object - ensure reactivity
        vm.settings = {
            //Preserve values set from external locations
            ...vm.settings,

            //Explicitly declare values which we'll be modifying
            touchPointTypeId: this.brick.config.touchPointTypeId || '',
            touchpointParentType: this.brick.config.touchpointParentType || '',
            customConditions: (this.brick.config.customConditions || []).map(i => ({ ...i })),
            typeSettings: {...(this.brick.config.typeSettings || {})},
            arrivalCondition: this.brick.config.arrivalCondition || '',
            goal: this.brick.config.goal
        }

        //Attempt to load all parameters used for default name generation at once to prevent accidentally breaking default name 
        //by switching prior to the labels used for generation having been loaded
        try {
            const parentTypes = await api.getTouchPointParentTypes();
            const types = await api.getTouchpointTypes();
            let campaignOptions = [];

            if(this.campaignType){
                campaignOptions = await this.campaignType.getOptions();
            }
            
            vm.touchPointParentTypes = parentTypes;
            vm.touchPointTypes = types;
            vm.campaignOptions = campaignOptions;
        } catch(e){
            bootstrapDangerGrowl(e);
        }
        
        //Our watch can't be safely attached at component initialization
        //our value may change multiple times breaking our configuration
        //as touchpointTypeId and touchpointParentType both change during the initialization step
        vm.$watch('displayGroupByTitle', value => {
            if(!value) {
                vm.removeTypeSetting('groupByTitle');
                return;
            }

            //Only add our type with a default if it wasn't already present as a boolean
            if(typeof this.settings.typeSettings.groupByTitle !== 'boolean'){
                vm.addTypeSetting('groupByTitle', true);
            }
        }, { 
            immediate: true 
        });
        
        vm.$watch(
            'campaignType',
            async (newValue, oldValue) => {
                //Remove any and all trace of our previous value
                if(oldValue) {
                    vm.campaignOptionsProm = Promise.resolve([]);
                    vm.campaignOptions = [];
                    vm.removeTypeSetting(oldValue.propertyName);
                }
                
                if(!newValue){
                    return;
                }
                
                //Ensure reactivity, don't reset our value by using the current value as default
                vm.addTypeSetting(
                    newValue.propertyName,
                    vm.settings.typeSettings[newValue.propertyName]
                );
                
                const campaignOptionsProm = newValue.getOptions();
                vm.campaignOptionsProm = campaignOptionsProm;
                const campaignOptions = await vm.campaignOptionsProm;
                
                //Guard against the user having switched to another campaign option
                if(vm.campaignOptionsProm === campaignOptionsProm){
                    vm.campaignOptions = campaignOptions;
                }
            },
            {
              immediate: true  
            }
        );
    },
    validations: {
        settings: {
            touchPointTypeId: {
                required
            },
            touchpointParentType: {
                required
            }
        }
    },
    methods: {
        displayErrors(){
            this.$v.$touch();
            
            if(this.$refs.customConditions){
                this.$refs.customConditions.triggerValidations();
            }
        },
        
        async save() {
            const vm = this;

            vm.initialCustomConditionIds = vm.settings.customConditions.map(c => c.id);

            //Having a new customCondition with only a type may be a mistake
            //prompt the user to ask them if they want to continue saving
            const hasNewTypedCustomConditionsWithoutValue = this.settings.customConditions.some(
                c => c.type &&
                    !c.value &&
                    vm.initialCustomConditionIds.indexOf(c.id) === -1
            );

            if(hasNewTypedCustomConditionsWithoutValue){
                try {
                    await promptYesNo(
                        this.resource('JourneyComposer_BrickSettingsTouchpoint_CustomConditions_NoValuePrompt_Title'),
                        this.resource('JourneyComposer_BrickSettingsTouchpoint_CustomConditions_NoValuePrompt_Body')
                    );
                } catch(e) {
                    return;
                }
            }

            const touchPointType = vm.touchPointTypes.find((type) =>
                type.id === vm.settings.touchPointTypeId
            );

            if(touchPointType) {
                vm.settings.icon = touchPointType.icon;
            }

            //Custom conditions with neither a value nor a type should be removed
            this.settings.customConditions = this.settings.customConditions.filter(
                c => !!c.type || !!c.value
            );

            this.saveSettings();
        },
        removeTypeSetting(name){
            this.$delete(this.settings.typeSettings, name);
        },
        addTypeSetting(name, defaultValue){
            this.$set(this.settings.typeSettings, name, defaultValue);
        },
        isParentType(expectedParentType){
            return this.settings.touchpointParentType === expectedParentType;
        },
        isSubType(...expectedSubTypes){
            if(!this.settings.touchPointTypeId){
                return false;
            }

            //Prevent casing shenanigans
            return expectedSubTypes.indexOf(this.settings.touchPointTypeId.toUpperCase()) !== -1;
        }
    },
    computed: {
        ...mapState('journeyBuilder/composer', ['availableConnectionConditions']),
        
        /**
         * https://datanext.atlassian.net/browse/DPN-2845
         * Generate a default name for the brick based on certain its configuration
         * 
         * A default name will consist of:
         * Campaign name + ParentType, when both a campaign and parent have been configured
         * SubType name, when no campaign has been configured
         * Brick type name, when no subtype has been configured
         * 
         * @return {String} default name
         */
        defaultName(){
            if(this.settings.touchpointParentType && this.campaignType && this.settings.typeSettings[this.campaignType.propertyName]){
                const labelParts = [];
                
                //Campaigns may not be loaded yet
                const campaign = this.campaignOptions.find(c => c.id === this.settings.typeSettings[this.campaignType.propertyName]);
                if(campaign){
                    labelParts.push(campaign.label);
                }

                labelParts.push(this.settings.touchpointParentType);
                
                return labelParts.join(' - ');
            }
            
            if(this.settings.touchPointTypeId){
                //TouchpointTypes may not be loaded yet
                if(this.selectedTouchPointType){
                    return this.selectedTouchPointType.name;
                }
                
                return '';
            }
            
            return brickDefinitions[this.brick.type].displayName;
        },
        touchpointParentTypeModel: {
            get(){
                return this.settings.touchpointParentType;
            },
            set(value){
                this.settings.touchpointParentType = value;
                this.$v.settings.touchpointParentType.$touch();
            }
        },
        selectedTouchPointType: {
            get(){
                const vm = this;
                return vm.touchPointTypes.find(t => t.id === this.settings.touchPointTypeId);
            },
            set(value){
                const vm = this;
                vm.settings.touchPointTypeId = value ? value.id : undefined;

                this.$v.settings.touchPointTypeId.$touch();
            }
        },
        availableArrivalConditions() {
            const vm = this;

            //Available conditioned are filtered by parent type "Web, etc.." and condition type "arrival"
            return vm.availableConnectionConditions.filter(
                (condition) => {
                    const supportedCondition = condition.touchPointTypes.find((type) => type === vm.settings.touchPointTypeId);
                    return supportedCondition && condition.type === conditionTypes.arrival;
                }
            );
        },
        availableTouchPointTypes() {
            const vm = this;
            
            return vm.touchPointTypes.filter(
                (type) => type.parentType === vm.settings.touchpointParentType
            );
        },
        displayGroupByTitle(){
            return this.isParentType(parentTypes.fb) || this.isSubType(subTypes.googleCSV, subTypes.dmVerzonden);
        },
        campaignType(){
            if(this.isParentType(parentTypes.email) && this.isSubType(subTypes.emailVerzonden)){
                return touchpointCampaignTypes.emailCampaign;
            }
            if(this.isParentType(parentTypes.tm) && this.isSubType(subTypes.telemarketing)){
                return touchpointCampaignTypes.steamCampaign;
            }
            if(this.isParentType(parentTypes.dm) && this.isSubType(subTypes.dmVerzonden)){
                return touchpointCampaignTypes.dmCampaign;
            }
            
            return undefined;
        }
    },
    watch: {
        settings: {
            handler: function (value) {
                const vm = this;
                const touchpoint = vm.touchPointTypes.find((tp) => tp.id === value.touchPointTypeId);

                //If the selected parent type no longer matches the selected touchpoint, clear selected touchpoint.
                if(touchpoint && touchpoint.parentType !== value.touchpointParentType) {
                    value.touchPointTypeId = undefined;
                    value.arrivalCondition = undefined;
                }
            },
            deep: true
        },
        defaultName(newDefaultName, oldDefaultName){
            //Update our name to the new default when the user hasn't entered a name themselves
            //If we are still using the brick type default (= brick created prior to this update), also update our brick name
            if(this.newName === oldDefaultName || this.newName === brickDefinitions[this.brickType].displayName){
                this.newName = newDefaultName;
            }
        }
    },
    data() {
        return {
            touchPointTypes: [],
            touchPointParentTypes: [],
            parentTypes,
            initialCustomConditionIds: [],
            
            isLoading: true,
            customConditionsValid: true,

            /**
             * @type {Promise<JourneyBuilder.DropdownOption[]>}
             */
            campaignOptionsProm: Promise.resolve([]),
            
            /**
             * @type {undefined|JourneyBuilder.DropdownOption[]}
             */
            campaignOptions: [],

            displayGoalToggleForTouchpointBricks: FLAGS.displayGoalToggleForTouchpointBricks
        };
    }
}
</script>

<style lang="scss" scoped>

</style> 